import { useEffect, useState, useRef } from "react";
import Icon from "components/shared/Icon";
import InlineErrors from "components/shared/InlineErrors";
import InlineDescription from "components/shared/InlineDescription";
import useKeyPress, { EVENT_KEYS } from "hooks/useKeyPress";

const getAriaLabel = (
	ariaLabel,
	resetValueLabel,
	label,
	required,
	requiredText
) => {
	if (ariaLabel === resetValueLabel) {
		ariaLabel = label;
	} else if (ariaLabel !== label) {
		ariaLabel = `${label} ${ariaLabel}`;
	}
	return `${ariaLabel} ${
		required && requiredText ? ` ${requiredText}` : ""
	}`.replace("*", "");
};

const sortNestedOptions = (nestedOptions) => {
	nestedOptions?.sort((optionA, optionB) => {
		return optionA.title.toUpperCase() > optionB.title.toUpperCase() ? 1 : -1;
	});
};

const InputNestedSelect = (props) => {
	const {
		id,
		name,
		required,
		requiredText,
		description,
		value = "",
		type,
		handleChange,
		validateInput,
		notEmptyMessage,
		removeInputValue,
		inlineErrors,
		showInlineErrors = true,
		isMobileView,
		disabled,
		backLabel = "",
		resetValueLabel = "",
		sort,
	} = props;
	const mainClass = "help-input-nested-select";
	const label = `${props.label}${required ? "*" : ""}`;

	const ref = useRef(null);
	const refButton = useRef(null);
	const [isOpened, setIsOpened] = useState(false);
	const [hasError, setHasError] = useState();
	const [clickedOutside, setClickedOutside] = useState(false);
	const [options, setOptions] = useState(
		props.options
			?.filter((option) => !!option.value)
			.map((option, index) => ({ ...option, index, depth: 0 })) || []
	);
	const [selectedOption, setSelectedOption] = useState({});
	const [selectedTemporalOption, setSelectedTemporalOption] = useState({
		title: label,
	});
	const displayableValue = isOpened
		? selectedTemporalOption.title
		: selectedOption.title || label;
	const [cursor, setCursor] = useState(-1);
	const downPress = useKeyPress(EVENT_KEYS.arrowdown);
	const upPress = useKeyPress(EVENT_KEYS.arrowup);
	let refsColletion = {};

	useEffect(() => {
		if (isOpened && options?.length && !upPress && downPress) {
			if (document.activeElement === refButton.current) {
				refsColletion?.[0]?.focus();
			} else {
				setCursor((prevState) => {
					return prevState < options?.length - 1 ? prevState + 1 : prevState;
				});
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [downPress, isOpened, upPress, options, selectedTemporalOption]);

	useEffect(() => {
		if (isOpened && options?.length && !downPress && upPress) {
			setCursor((prevState) => {
				return prevState > 0 ? prevState - 1 : prevState;
			});
			if (cursor === 0) refButton?.current.focus();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [upPress, isOpened, downPress, options, selectedTemporalOption]);

	useEffect(() => {
		if (selectedOption) {
			setCursor(selectedOption.index);
		} else {
			setCursor(-1);
		}
	}, [selectedOption]);

	useEffect(() => {
		if (cursor === -1) return;
		refsColletion?.[cursor]?.focus();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [cursor, selectedTemporalOption]);

	useEffect(() => {
		const handleClickOutside = (event) => {
			if (!ref.current?.contains(event.target)) {
				setClickedOutside(true);
				setCursor(-1);
			}
		};
		document.addEventListener("click", handleClickOutside, true);
		return () => window.removeEventListener("click", handleClickOutside, true);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if (inlineErrors?.[name]) {
			setHasError(true);
		} else {
			setHasError(false);
		}
	}, [inlineErrors, name]);

	useEffect(() => {
		if (clickedOutside) {
			if (isOpened) {
				if (value) {
					if (
						Array.isArray(selectedOption.parents) &&
						selectedOption.parents.length
					) {
						const parentOptions =
							selectedOption.parents[selectedOption.parents.length - 1];
						const newParentOptions = [
							...selectedOption.parents.slice(
								0,
								selectedOption.parents.length - 1
							),
						];
						setOptions(parentOptions?.[0]);
						if (newParentOptions.length) {
							setSelectedTemporalOption({
								title: backLabel,
								showBackLabel: true,
								parents: newParentOptions,
							});
						} else {
							setSelectedTemporalOption({ title: resetValueLabel });
						}
					}
				} else {
					setSelectedTemporalOption({ title: label });
					setOptions(props.options);
				}
				setIsOpened(false);
			}
			setClickedOutside(false);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [clickedOutside]);

	const toggleCustomOptionsListVisibility = () => {
		setCursor(
			!isOpened
				? selectedOption?.index
					? selectedOption.index
					: selectedTemporalOption?.index || 0
				: 0
		);
		if (isOpened) {
			if (
				Array.isArray(selectedTemporalOption.parents) &&
				selectedTemporalOption.parents.length
			) {
				const previousParentOptions =
					selectedTemporalOption.parents[
						selectedTemporalOption.parents.length - 1
					]?.[0];
				const newParentOptions = [
					...selectedTemporalOption.parents.slice(
						0,
						selectedTemporalOption.parents.length - 1
					),
				];
				setOptions(previousParentOptions);
				if (newParentOptions.length) {
					setSelectedTemporalOption({
						...previousParentOptions?.[0],
						title: backLabel,
						showBackLabel: true,
						parents: newParentOptions,
						index: cursor,
					});
				} else {
					setSelectedTemporalOption({
						...previousParentOptions?.[0],
						title: resetValueLabel,
						parents: newParentOptions,
					});
				}
			} else {
				removeInputValue(name);
				setSelectedTemporalOption({ title: label });
				setSelectedOption({});
				setIsOpened(!isOpened);
			}
		} else {
			setIsOpened(!isOpened);
			if (selectedTemporalOption.parents?.length) {
				setSelectedTemporalOption({
					...selectedTemporalOption,
					title: backLabel,
					showBackLabel: true,
				});
			} else {
				setSelectedTemporalOption({
					...selectedTemporalOption,
					title: resetValueLabel,
				});
			}
		}
	};
	const selectCustomOption = (event, option, key, text, idx) => {
		if (Array.isArray(option.options) && option.options.length) {
			if (sort) sortNestedOptions(option.options);
			setOptions(option.options);
			setSelectedTemporalOption({
				...option.options[0],
				title: backLabel,
				showBackLabel: true,
				parents: [...(selectedTemporalOption.parents || []), [options]],
				index: cursor,
			});
			setCursor(0);
		} else {
			handleChange &&
				handleChange({ ...event, target: { name, value: key, type } });
			setSelectedTemporalOption({
				...option,
				title: text,
				parents: [...(selectedTemporalOption.parents || [])],
				index: idx,
			});
			setSelectedOption({
				...option,
				title: text,
				parents: [...(selectedTemporalOption.parents || []), [options]],
				index: idx,
			});
			setIsOpened(!isOpened);
		}
	};

	const handleKeyboardNavigationOnButton = (event) => {
		if (isOpened && event.key === EVENT_KEYS.arrowleft.string) {
			toggleCustomOptionsListVisibility();
		} else if (event.key === EVENT_KEYS.escape.string) {
			setClickedOutside(true);
			setCursor(-1);
			refButton?.current.focus();
		}
	};

	const handleKeyboardNavigationOnList = (event, option, key, text, idx) => {
		if (
			event.key === EVENT_KEYS.arrowright.string &&
			Array.isArray(option.options) &&
			option.options.length
		) {
			selectCustomOption(event, option, key, text, idx);
		} else if (event.key === EVENT_KEYS.enter.string) {
			selectCustomOption(event, option, key, text, idx);
			event.preventDefault();
			refButton?.current.focus();
		} else if (event.key === EVENT_KEYS.escape.string) {
			setClickedOutside(true);
			setCursor(-1);
			refButton?.current.focus();
		}
	};

	return (
		<div ref={ref} className={mainClass}>
			{description?.modalContent && (
				<InlineDescription
					{...description}
					isMobileView={isMobileView}
					position="top"
				/>
			)}
			<div className={`${mainClass}__custom-container`}>
				<button
					ref={refButton}
					id="custom-dropdown-button"
					aria-haspopup="listbox"
					className={`${mainClass}__custom-container__trigger-button${
						value
							? ` ${mainClass}__custom-container__trigger-button--item-selected`
							: ""
					}${
						hasError
							? ` ${mainClass}__custom-container__trigger-button--invalid`
							: ""
					}${
						disabled
							? ` ${mainClass}__custom-container__trigger-button--disabled`
							: ""
					}${
						isOpened
							? ` ${mainClass}__custom-container__trigger-button--opened`
							: ""
					}${
						isOpened && selectedTemporalOption.showBackLabel
							? ` ${mainClass}__custom-container__trigger-button--back-label`
							: ""
					}`}
					onClick={toggleCustomOptionsListVisibility}
					onBlur={() => {
						required &&
							notEmptyMessage &&
							validateInput &&
							validateInput(
								name,
								value,
								required,
								notEmptyMessage,
								null,
								null,
								setHasError
							);
					}}
					onKeyDown={(event) => {
						handleKeyboardNavigationOnButton(event);
					}}
					disabled={disabled}
					aria-controls="expandable-content"
					aria-expanded={isOpened}
					aria-label={getAriaLabel(
						displayableValue,
						resetValueLabel,
						label,
						required,
						requiredText
					)}
					aria-describedby={id}
					data-testid="input-nested-select-button"
				>
					<div
						className={`${mainClass}__custom-container__trigger-button__label${
							value && !isOpened
								? ""
								: ` ${mainClass}__custom-container__trigger-button__label--invisible`
						}`}
					>
						{label}
					</div>
					<div
						className={`${mainClass}__custom-container__trigger-button__text-container`}
					>
						{isOpened && selectedTemporalOption.showBackLabel && (
							<Icon icon="previous-2" />
						)}
						<span>{displayableValue}</span>
						<Icon
							icon={isOpened ? "norgie-up" : "norgie-opened"}
							loading={"eager"}
						/>
					</div>
				</button>
				{isOpened && (
					<ul
						id="custom-dropdown-options-list"
						className={`${mainClass}__custom-container__options-list${
							isOpened
								? ""
								: ` ${mainClass}__custom-container__options-list--hidden`
						}`}
						role="listbox"
						aria-activedescendant={`option-${value}`}
						tabIndex="-1"
						hidden={!isOpened}
						aria-label={label}
						data-testid="input-nested-select"
					>
						{options?.map((option, idx) => {
							const key = option.key || option.value;
							const text = option.title || option.text;
							const hasOptions = !!option.options?.length;
							return (
								<li
									key={idx}
									className={`${mainClass}__custom-container__options-list__option-item${
										key === value
											? ` ${mainClass}__custom-container__options-list__option-item--selected`
											: ""
									}`}
									id={`option-${key}`}
									role={hasOptions ? "menu" : "option"}
									aria-selected={key === value}
									onMouseDown={(event) => {
										selectCustomOption(event, option, key, text, idx);
									}}
									onKeyDown={(event) => {
										handleKeyboardNavigationOnList(
											event,
											option,
											key,
											text,
											idx
										);
									}}
									tabIndex="0"
									ref={(instance) => {
										refsColletion[idx] = instance;
									}}
								>
									<div
										className={`${mainClass}__custom-container__options-list__option-item__value`}
									>
										{text}
									</div>
									{hasOptions && <Icon icon="next-2" />}
								</li>
							);
						})}
					</ul>
				)}
			</div>
			{inlineErrors && showInlineErrors && (
				<InlineErrors validate={id} errorMessages={inlineErrors} />
			)}
			{description?.text && (
				<InlineDescription
					{...description}
					isMobileView={isMobileView}
					position="bottom"
				/>
			)}
		</div>
	);
};

export default InputNestedSelect;
